// ============================================================================
// MODULE DATING - TYPES TYPESCRIPT
// Préfixe: Dating
// ============================================================================

// ============================================================================
// ENUMS
// ============================================================================

export type DatingGender = 'male' | 'female' | 'non_binary' | 'other';

export type DatingLookingFor = 'male' | 'female' | 'both' | 'everyone';

export type DatingInteractionType = 'like' | 'dislike' | 'super_like';

export type DatingMatchStatus = 'active' | 'unmatched' | 'blocked';

export type DatingSubscriptionStatus = 'active' | 'expired' | 'cancelled' | 'pending';

export type DatingMessageStatus = 'sent' | 'delivered' | 'read';

export type DatingOrderStatus = 'pending' | 'paid' | 'failed' | 'refunded';

// ============================================================================
// INTERFACES PRINCIPALES
// ============================================================================

/**
 * Plan d'abonnement Dating
 */
export interface DatingPlan {
  id: string;
  tenant_id: number;
  domain?: string;
  
  // Informations
  name: string;
  slug: string;
  description?: string;
  
  // Tarification
  price: number;
  currency: string;
  duration_days: number;
  
  // Fonctionnalités
  is_free: boolean;
  is_vip: boolean;
  features: string[];
  
  // Limites
  daily_likes_limit?: number;
  daily_super_likes_limit?: number;
  daily_messages_limit?: number;
  
  // Affichage
  display_order: number;
  badge_color: string;
  is_active: boolean;
  is_popular: boolean;
  
  // Timestamps
  created_at: string;
  updated_at: string;
}

/**
 * Photo de profil
 */
export interface DatingPhoto {
  id: string;
  profile_id: string;
  url: string;
  is_primary: boolean;
  order: number;
  uploaded_at?: string;
}

/**
 * Profil de rencontre
 */
export interface DatingProfile {
  id: string;
  user_id?: string;
  tenant_id: number;
  domain?: string;
  
  // Informations de base
  display_name: string;
  email: string;
  phone?: string;
  
  // Profil
  bio?: string;
  birth_date: string;
  gender: DatingGender;
  looking_for: DatingLookingFor;
  
  // Localisation
  // ⚠️ location n'est JAMAIS exposé via l'API publique (sécurité)
  // Utilisé uniquement côté admin ou pour le profil propriétaire
  location?: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  } | null;
  city?: string;
  country: string;
  location_visible: boolean;
  
  // Préférences de recherche
  min_age: number;
  max_age: number;
  max_distance_km: number;
  search_min_age?: number;
  search_max_age?: number;
  search_max_distance?: number;
  
  // Paramètres de confidentialité
  show_online_status?: boolean;
  show_distance?: boolean;
  show_last_active?: boolean;
  
  // Photos
  photos: DatingPhoto[];
  avatar_url?: string;
  
  // Intérêts
  interests: string[];
  languages: string[];
  occupation?: string;
  education?: string;
  height_cm?: number;
  
  // Statut VIP
  is_vip: boolean;
  vip_until?: string;
  
  // Vérification
  is_verified: boolean;
  verified_at?: string;
  verification_method?: string;
  
  // Statut
  is_active: boolean;
  is_visible: boolean;
  is_online: boolean;
  last_active_at: string;
  
  // Statistiques
  total_likes_received: number;
  total_likes_given: number;
  total_matches: number;
  total_super_likes_received: number;
  profile_views: number;
  
  // Boost
  is_boosted: boolean;
  boost_until?: string;
  
  // Blocage
  is_banned: boolean;
  ban_reason?: string;
  banned_at?: string;
  
  // Auth token (pour utilisateurs non-Supabase)
  access_token?: string;
  token_expires_at?: string;
  
  // Timestamps
  created_at: string;
  updated_at: string;
}

/**
 * Profil simplifié pour les suggestions (swipe)
 * ⚠️ SÉCURITÉ: Ne contient JAMAIS les coordonnées GPS exactes
 * La distance est floutée (arrondie à 0.5km) côté serveur
 */
export interface DatingSuggestion {
  id: string;
  display_name: string;
  bio?: string;
  birth_date: string;
  age: number;
  gender: DatingGender;
  city?: string;
  country?: string;
  /** Photos du profil (format JSONB depuis la RPC) */
  photos: Array<{ url: string; is_primary?: boolean; order?: number; id?: string; profile_id?: string }>;
  avatar_url?: string;
  interests: string[];
  occupation?: string;
  is_vip: boolean;
  is_verified: boolean;
  is_online: boolean;
  last_active_at: string;
  /** Distance floutée en km (arrondie à 0.5km pour protéger la vie privée) */
  distance_km: number | null;
  /** Score de compatibilité (0-100) */
  match_score: number;
  // ❌ PAS DE CHAMP location - les coordonnées exactes ne sont JAMAIS exposées
}

/**
 * Abonnement Dating
 */
export interface DatingSubscription {
  id: string;
  profile_id: string;
  plan_id: string;
  tenant_id: number;
  domain?: string;
  
  // Période
  started_at: string;
  expires_at: string;
  
  // Statut
  status: DatingSubscriptionStatus;
  is_active: boolean;
  
  // Paiement
  payment_reference?: string;
  payment_method?: string;
  amount_paid?: number;
  currency: string;
  
  // Historique
  activated_at?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  
  // Timestamps
  created_at: string;
  updated_at: string;
  
  // Relations
  plan?: DatingPlan;
}

/**
 * Interaction (Like/Dislike/SuperLike)
 */
export interface DatingInteraction {
  id: string;
  from_profile_id: string;
  to_profile_id: string;
  tenant_id: number;
  domain?: string;
  interaction_type: DatingInteractionType;
  created_at: string;
  
  // Relations
  from_profile?: DatingProfile;
  to_profile?: DatingProfile;
}

/**
 * Match (correspondance mutuelle)
 */
export interface DatingMatch {
  id: string;
  profile1_id: string;
  profile2_id: string;
  tenant_id: number;
  domain?: string;
  status: DatingMatchStatus;
  initiated_by?: string;
  matched_at: string;
  unmatched_at?: string;
  unmatched_by?: string;
  
  // Relations
  profile1?: DatingProfile;
  profile2?: DatingProfile;
  conversation?: DatingConversation;
}

/**
 * Conversation de chat
 */
export interface DatingConversation {
  id: string;
  match_id: string;
  tenant_id: number;
  domain?: string;
  participant1_id: string;
  participant2_id: string;
  last_message_at?: string;
  last_message_preview?: string;
  last_message_by?: string;
  unread_count_p1: number;
  unread_count_p2: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  
  // Relations
  participant1?: DatingProfile;
  participant2?: DatingProfile;
  messages?: DatingMessage[];
}

/**
 * Message de chat
 */
export interface DatingMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  tenant_id: number;
  domain?: string;
  content: string;
  message_type: 'text' | 'image' | 'gif' | 'audio';
  media_url?: string;
  status: DatingMessageStatus;
  sent_at: string;
  delivered_at?: string;
  read_at?: string;
  is_deleted: boolean;
  deleted_at?: string;
  
  // Relations
  sender?: DatingProfile;
}

/**
 * Blocage
 */
export interface DatingBlock {
  id: string;
  blocker_id: string;
  blocked_id: string;
  tenant_id: number;
  domain?: string;
  reason?: string;
  created_at: string;
}

/**
 * Signalement
 */
export interface DatingReport {
  id: string;
  reporter_id: string;
  reported_id: string;
  tenant_id: number;
  domain?: string;
  reason: string;
  description?: string;
  evidence_urls: string[];
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  reviewed_by?: string;
  reviewed_at?: string;
  resolution?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Limites quotidiennes
 */
export interface DatingDailyLimits {
  id: string;
  profile_id: string;
  tenant_id: number;
  domain?: string;
  date: string;
  likes_count: number;
  super_likes_count: number;
  messages_count: number;
  created_at: string;
  updated_at: string;
}

/**
 * Commande Dating (achat de pass)
 */
export interface DatingOrder {
  id: string;
  order_number: string;
  profile_id: string;
  plan_id: string;
  tenant_id: number;
  domain?: string;
  
  // Client
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  
  // Montant
  amount: number;
  currency: string;
  
  // Coupon
  coupon_code?: string;
  discount_amount: number;
  
  // Paiement
  payment_method?: string;
  payment_reference?: string;
  payment_url?: string;
  payment_data?: Record<string, any>;
  
  // Statut
  status: DatingOrderStatus;
  
  // Timestamps
  created_at: string;
  updated_at: string;
  paid_at?: string;
  
  // Relations
  plan?: DatingPlan;
  profile?: DatingProfile;
}

// ============================================================================
// TYPES DE REQUÊTES / RÉPONSES
// ============================================================================

/**
 * Paramètres pour créer/mettre à jour un profil
 */
export interface DatingProfileInput {
  display_name?: string;
  email?: string;
  phone?: string;
  bio?: string;
  birth_date?: string;
  gender?: DatingGender;
  looking_for?: DatingLookingFor;
  latitude?: number;
  longitude?: number;
  city?: string;
  country?: string;
  location_visible?: boolean;
  min_age?: number;
  max_age?: number;
  max_distance_km?: number;
  search_min_age?: number;
  search_max_age?: number;
  search_max_distance?: number;
  interests?: string[];
  languages?: string[];
  occupation?: string;
  education?: string;
  height_cm?: number;
  is_visible?: boolean;
  show_online_status?: boolean;
  show_distance?: boolean;
  show_last_active?: boolean;
}

/**
 * Paramètres pour les suggestions
 */
export interface DatingSuggestionsParams {
  latitude?: number;
  longitude?: number;
  radius_km?: number;
  limit?: number;
  offset?: number;
}

/**
 * Résultat d'une interaction
 */
export interface DatingInteractionResult {
  success: boolean;
  interaction_id?: string;
  is_match: boolean;
  match_id?: string;
  conversation_id?: string;
  error?: string;
}

/**
 * Résultat d'envoi de message
 */
export interface DatingSendMessageResult {
  success: boolean;
  message_id?: string;
  error?: string;
}

/**
 * Paramètres pour créer une commande
 */
export interface DatingOrderInput {
  plan_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  payment_method: string;
  coupon_code?: string;
}

/**
 * Statistiques du profil
 */
export interface DatingProfileStats {
  total_likes_received: number;
  total_likes_given: number;
  total_matches: number;
  total_super_likes_received: number;
  profile_views: number;
  active_conversations: number;
  unread_messages: number;
}

/**
 * Contexte d'authentification Dating
 */
export interface DatingAuthContext {
  profile: DatingProfile | null;
  subscription: DatingSubscription | null;
  isAuthenticated: boolean;
  isVip: boolean;
  dailyLimits: DatingDailyLimits | null;
  remainingLikes: number;
  remainingSuperLikes: number;
  remainingMessages: number;
  // Données temps réel partagées
  unreadCount: number;
  conversations: DatingConversation[];
  refreshConversations: () => Promise<void>;
}

// ============================================================================
// CONSTANTES
// ============================================================================

export const DATING_INTERESTS = [
  'sport', 'musique', 'voyage', 'cuisine', 'lecture', 'cinema', 'art',
  'photographie', 'danse', 'jeux_video', 'nature', 'animaux', 'mode',
  'technologie', 'fitness', 'yoga', 'meditation', 'theatre', 'concerts',
  'restaurants', 'cafe', 'vin', 'biere', 'randonnee', 'plage', 'montagne',
  'camping', 'jardinage', 'bricolage', 'voitures', 'motos', 'velo',
  'course', 'natation', 'football', 'basketball', 'tennis', 'golf',
  'spiritualite', 'politique', 'histoire', 'sciences', 'langues',
  'ecriture', 'podcast', 'series', 'anime', 'manga', 'comics'
] as const;

export const DATING_LANGUAGES = [
  'Français', 'English', 'Español', 'Deutsch', 'Italiano', 'Português',
  'العربية', '中文', '日本語', '한국어', 'हिन्दी', 'Русский',
  'Pidgin', 'Ewondo', 'Douala', 'Bamiléké', 'Fulfulde', 'Hausa'
] as const;

export const DATING_REPORT_REASONS = [
  { value: 'fake_profile', label: 'Faux profil' },
  { value: 'harassment', label: 'Harcèlement' },
  { value: 'inappropriate_content', label: 'Contenu inapproprié' },
  { value: 'spam', label: 'Spam / Publicité' },
  { value: 'underage', label: 'Utilisateur mineur' },
  { value: 'scam', label: 'Arnaque' },
  { value: 'other', label: 'Autre' }
] as const;

export const DATING_GENDER_LABELS: Record<DatingGender, string> = {
  male: 'Homme',
  female: 'Femme',
  non_binary: 'Non-binaire',
  other: 'Autre'
};

export const DATING_LOOKING_FOR_LABELS: Record<DatingLookingFor, string> = {
  male: 'Hommes',
  female: 'Femmes',
  both: 'Hommes et Femmes',
  everyone: 'Tout le monde'
};
